import React, { useMemo } from 'react';
import { Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { PermissionsHelper } from 'benchmarking-domain/permissions-helper';
import Project from 'benchmarking-domain/project';
import ProjectProgressStatistics from 'benchmarking-domain/project-progress-statistics';
import ProjectSection from 'benchmarking-domain/project-section';
import Questionnaire from 'benchmarking-domain/questionnaire';
import { ProgressBar } from 'components/core/shared/progress-bar';
import { useUsersEmailToNameMap } from 'hooks/use-users-email-to-name-map';

import { useUser } from '../../../hooks/use-user';

interface ProgressSummaryProps {
  project: Project;
  projectSections: ProjectSection[] | undefined;
  projectProgressStatistics?: ProjectProgressStatistics;
  questionnaires: Questionnaire[] | undefined;
}

interface UserProjectProgress {
  user: string;
  count: number;
  total: number;
}

const calculateProjectProgressPerUser = (
  projectProgress: ProjectProgressStatistics | undefined,
  projectSections: ProjectSection[],
  allowedSections: Set<string | undefined>,
  includeUnassigned: boolean
) => {
  const users: UserProjectProgress[] = [];
  const assignedSections = new Set<string>();
  const allUsers = new Set(projectSections.map((ps) => ps.managers).flat());

  allUsers.forEach((user) => {
    const userSections = projectSections.filter((ps) => ps.managers.includes(user));
    let count = 0;
    let total = 0;

    userSections.forEach((section) => {
      if (allowedSections.has(section.id) === false) {
        return;
      }

      assignedSections.add(section.id);

      count += projectProgress?.countInSections?.[section.id] ?? 0;
      total += projectProgress?.totalInSections?.[section.id] ?? 0;
    });

    if (total > 0 || count > 0) {
      users.push({ user, count, total });
    }
  });

  const orderedUsers = users.sort((a, b) => a.user.localeCompare(b.user));

  let totalUnassigned = 0;
  let countUnassigned = 0;
  Object.entries(projectProgress?.totalInSections ?? {}).forEach(([sectionName, total]) => {
    if (assignedSections.has(sectionName) || allowedSections.has(sectionName) === false) {
      return;
    }

    totalUnassigned += total;
    countUnassigned += projectProgress?.countInSections?.[sectionName] ?? 0;
  });
  if (includeUnassigned && (totalUnassigned > 0 || countUnassigned > 0)) {
    orderedUsers.push({ user: 'Unassigned', count: countUnassigned, total: totalUnassigned });
  }

  return orderedUsers;
};

const CustomProgressBar: React.FC<{ user: UserProjectProgress }> = ({ user }) => {
  const progressValue = Math.min(100, (user.count / user.total) * 100);
  const progressColor = progressValue < 50 ? 'error' : progressValue < 75 ? 'warning' : 'primary';
  return <ProgressBar value={progressValue} color={progressColor} />;
};

export const ProgressSummary: React.FC<ProgressSummaryProps> = ({
  project,
  projectSections,
  questionnaires,
  projectProgressStatistics,
}) => {
  const { usersEmailToNameMap } = useUsersEmailToNameMap();
  const { user } = useUser();

  const allowedSectionsForProject = useMemo(() => {
    return new Set(
      questionnaires
        ?.map((q) =>
          q.sections.filter((s) => s.technologyFilter?.some((t) => t === project.technology) ?? true).map((x) => x.id)
        )
        .flat() ?? []
    );
  }, [project, questionnaires]);

  const usersSummary = useMemo(
    () =>
      calculateProjectProgressPerUser(
        projectProgressStatistics,
        projectSections ?? [],
        allowedSectionsForProject,
        PermissionsHelper.hasUserAtLeastRole('projectManager', user, project)
      ),
    [allowedSectionsForProject, projectSections, projectProgressStatistics, project, user]
  );

  if (!projectProgressStatistics || usersSummary.length === 0) {
    return <Typography variant="body2">Missing progress statistics, nothing to display.</Typography>;
  }

  return (
    <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell colSpan={2}>Progress</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usersSummary.map((u) => (
              <TableRow key={u.user}>
                <TableCell sx={{ width: 'fit-content' }}>{usersEmailToNameMap?.get(u.user) ?? u.user}</TableCell>
                <TableCell sx={{ minWidth: '250px' }}>
                  <CustomProgressBar user={u} />
                </TableCell>
                <TableCell sx={{ minWidth: '130px', width: '130px' }}>
                  {u.count} / {u.total}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};
